import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { CheckInClassForm } from "@/modules/attendance/presentation/ui/CheckInClassForm";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Badge, Button, confirm, Dialog, DialogContent, DialogPortal, DialogTitle, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { CheckCircleIcon, CheckIcon, EditIcon, XIcon } from "lucide-react";
import { useState } from "react";
import {
  useCancelClassSession,
  useClassSessionChangeLogsQuery,
  useClassSessionQuery,
  useCompleteClassSession,
  useDuplicateClassSession,
} from "../api/queries";
import { ClassSessionAttendanceSummary } from "../ui/ClassSessionAttendanceSummary";
import { ClassSessionChangeLogTable } from "../ui/ClassSessionChangeLogTable";
import { ClassSessionGeneralInfo } from "../ui/ClassSessionGeneralInfo";
import { ClassSessionInstructorAttendanceCard } from "../ui/ClassSessionInstructorAttendanceCard";
import { MoveSessionDialog } from "../ui/MoveSessionDialog";
import { SessionEngagementBadges } from "../ui/session-display";

export function ClassSessionDetailPage() {
  const nav = useNavigate({ from: "/app/classes/sessions/$id" });
  const { id } = useParams({ from: "/app/classes/sessions/$id" });
  const { data: session, ...sessionResult } = useClassSessionQuery(id);
  const { data: changeLogsData, ...changeLogsResult } =
    useClassSessionChangeLogsQuery(id);
  const completeSession = useCompleteClassSession(id);
  const duplicateSession = useDuplicateClassSession();
  const cancelSession = useCancelClassSession();

  const canUpdate = useActionPermission(["classes:update"]);
  const canCreate = useActionPermission(["classes:create"]);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);

  const canOpenInstructorCheckIn =
    canUpdate && session?.status === "completed";

  const handleEdit = () => {
    nav({
      to: "/app/classes/sessions/$id/edit",
      params: { id },
    });
  };

  const handleDuplicate = async () => {
    const ok = await confirm({
      title: "ສ້າງຊົ່ວໂມງຮຽນໃໝ່",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະສ້າງ ຊົ່ວໂມງຮຽນ ໃໝ່ຈາກ ຊົ່ວໂມງຮຽນ ນີ້?",
      actionText: "ສ້າງ",
    });
    if (ok) {
      try {
        const newSession = await duplicateSession.mutateAsync(id);
        nav({
          to: "/app/classes/sessions/$id",
          params: { id: newSession.id },
        });
      } catch {
        // Error handled by mutation
      }
    }
  };

  const handleComplete = async () => {
    const ok = await confirm({
      title: "ສຳເລັດຊົ່ວໂມງຮຽນ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະສຳເລັດ ຊົ່ວໂມງຮຽນ ນີ້?",
      actionText: "ສຳເລັດ",
    });
    if (ok) {
      try {
        await completeSession.mutateAsync();
        sessionResult.refetch();
      } catch {
        // Error handled by mutation
      }
    }
  };

  const handleCancel = async () => {
    const ok = await confirm({
      title: "ຍົກເລີກຊົ່ວໂມງຮຽນ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກ ຊົ່ວໂມງຮຽນ ນີ້?",
      actionText: "ຍົກເລີກ",
      ActionProps: {
        variant: "destructive",
      },
    });
    if (ok) {
      try {
        await toast.promise(cancelSession.run(id, "ຍົກເລີກໂດຍ admin"), {
          loading: "ກຳລັງຍົກເລີກ...",
          success: "ຍົກເລີກ ຊົ່ວໂມງຮຽນ ສຳເລັດ",
          error: "ຍົກເລີກ ຊົ່ວໂມງຮຽນ ລົ້ມເຫຼວ",
        });
        sessionResult.refetch();
      } catch {
        // Error handled by toast
      }
    }
  };

  const getStatusBadge = () => {
    if (session?.status === "scheduled") {
      return (
        <Badge
          variant="secondary"
          className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
        >
          SCHEDULED
        </Badge>
      );
    }
    if (session?.status === "completed") {
      return <Badge variant="default">COMPLETED</Badge>;
    }
    return <Badge variant="destructive">CANCELLED</Badge>;
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຈັດຕາຕະລາງຄລາດສ໌", to: "/app/classes/sessions" },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        <QueryState
          result={sessionResult}
          title="ກຳລັງໂຫຼດຊົ່ວໂມງຮຽນ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!session ? null : (
            <div className="space-y-6">
              {/* Title and Actions */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <h1 className="font-bold text-3xl tracking-tight">
                    {session.templateName}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2">
                    {getStatusBadge()}
                    <SessionEngagementBadges session={session} />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {canOpenInstructorCheckIn && (
                    <Button
                      className="bg-primary text-primary-foreground"
                      onClick={() => setCheckInOpen(true)}
                    >
                      <CheckCircleIcon className="mr-2 size-4" />
                      ກວດຊື່ຄູສອນ
                    </Button>
                  )}
                  {canUpdate && (
                    <>
                      {session.status === "scheduled" && (
                        <>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={handleComplete}
                            disabled={completeSession.isPending}
                            title="ສຳເລັດ"
                          >
                            <CheckIcon className="size-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={handleCancel}
                            disabled={cancelSession.isPending}
                            title="ຍົກເລີກ"
                          >
                            <XIcon className="size-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="default"
                        onClick={handleEdit}
                        className="bg-primary text-primary-foreground"
                      >
                        <EditIcon className="mr-2 size-4" />
                        ແກ້ໄຂ ຊົ່ວໂມງຮຽນ
                      </Button>
                      {/* {session.status === "scheduled" && (
                        <Button
                          variant="outline"
                          onClick={() => setMoveDialogOpen(true)}
                        >
                          <Move className="mr-2 size-4" />
                          ຍ້າຍຊົ່ວໂມງຮຽນ
                        </Button>
                      )} */}
                    </>
                  )}
                </div>
              </div>

              <MoveSessionDialog
                session={session}
                open={moveDialogOpen}
                onOpenChange={setMoveDialogOpen}
                onSuccess={() => {
                  sessionResult.refetch();
                  changeLogsResult.refetch();
                }}
              />

              {/* Content Grid */}
              <div className="grid gap-6 lg:grid-cols-3">
                {/* Left Column - General Info */}
                <div className="space-y-6 lg:col-span-2">
                  <ClassSessionGeneralInfo session={session} />
                  <ClassSessionChangeLogTable
                    logs={changeLogsData?.logs ?? []}
                    isLoading={changeLogsResult.isLoading}
                  />
                </div>

                {/* Right Column - Attendance Summary */}
                <div className="space-y-6 lg:col-span-1">
                  <ClassSessionAttendanceSummary session={session} />
                  <ClassSessionInstructorAttendanceCard
                    session={session}
                    canCorrect={canUpdate}
                    onCheckIn={
                      canOpenInstructorCheckIn
                        ? () => setCheckInOpen(true)
                        : undefined
                    }
                    onCorrected={() => {
                      sessionResult.refetch();
                      changeLogsResult.refetch();
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </QueryState>
      </Main>

      {session && (
        <Dialog open={checkInOpen} onOpenChange={setCheckInOpen}>
          <DialogPortal>
            <DialogContent className="h-full max-h-[90vh] max-w-4xl gap-0 overflow-y-auto p-0 sm:max-w-4xl">
              <div className="flex flex-col p-6">
                <DialogTitle className="mb-4 font-semibold text-lg">
                  ກວດຊື່ຄູສອນ
                </DialogTitle>
                <CheckInClassForm
                  sessionId={session.id}
                  sessionStatus={session.status}
                  capacity={session.capacity}
                  attendanceCount={session.attendanceCount ?? 0}
                  instructorIds={session.instructorIds}
                  instructorNames={session.instructorNames}
                  instructors={session.instructors}
                  sessionDate={session.date}
                  onSuccess={() => {
                    setCheckInOpen(false);
                    sessionResult.refetch();
                  }}
                  onCheckInComplete={() => {
                    setCheckInOpen(false);
                    sessionResult.refetch();
                  }}
                  onCancel={() => setCheckInOpen(false)}
                />
              </div>
            </DialogContent>
          </DialogPortal>
        </Dialog>
      )}
    </>
  );
}
